import { css } from 'styled-components';

import fontService, { FontWeight } from './FontService';
import { tablet } from './ui';

/**
 * Типографика карьерного раздела (ТЗ 0070).
 *
 * 🔴 Общая шкала проекта выразить требования ТЗ НЕ МОЖЕТ. FontService даёт
 * small = 12, normal = 14, h1 = 54; весь карьерный раздел построен на
 * 12 px основного текста и 11 px метаданных — то есть нарушает ТЗ по
 * построению, а не по недосмотру. ТЗ требует основной 16, метаданные не
 * меньше 14, H1 40–48.
 *
 * Почему не поправить общую шкалу: она красит ВЕСЬ LMS, и правка ради одного
 * раздела перевёрстывает журналы, уроки и расписание — то есть меняет то,
 * чего никто не просил, ради того, о чём просили.
 *
 * ⚠️ getLineHeight принимает ПРОЦЕНТЫ: 1.45–1.6 из ТЗ — это 145–160.
 */
/**
 * Ступени раздела (ТЗ «визуальное упрощение», 15.09): desktop от 1200,
 * планшет 768–1199. Проектный `desktop` начинается с 1220 — ТЗ ему не
 * соответствует, поэтому у оболочки раздела свои запросы.
 */
export const careerDesktop = '@media (min-width: 1200px)';
export const careerTablet = '@media (min-width: 768px) and (max-width: 1199px)';

/** Внешние поля широкой оболочки: шапка, меню с содержимым и подвал — один край. */
export const careerShellPadding = css`
  padding-inline: 16px;

  ${tablet} {
    padding-inline: 24px;
  }

  @media (min-width: 1200px) {
    padding-inline: 32px;
  }
`;

export const careerType = {
  /**
   * Шкала «визуального упрощения» (15.09): меньше жирного. 600 — только
   * заголовок страницы и название действия; разделы и этапы — 500.
   */
  h1: () => css`
    ${fontService.getFont({ size: 28, lineHeight: 128.57, weight: FontWeight.semibold })}

    ${tablet} {
      ${fontService.getFont({ size: 32, lineHeight: 125, weight: FontWeight.semibold })}
    }
  `,
  /** Название основного действия: 24/32, на телефоне 22/30. */
  actionTitle: () => css`
    ${fontService.getFont({ size: 22, lineHeight: 136.36, weight: FontWeight.semibold })}

    ${tablet} {
      ${fontService.getFont({ size: 24, lineHeight: 133.33, weight: FontWeight.semibold })}
    }
  `,
  /** Заголовок раздела: 20/28. */
  h2: () => css`
    ${fontService.getFont({ size: 20, lineHeight: 140, weight: FontWeight.medium })}
  `,
  /** Название этапа, заголовок карточки: 18/26. */
  h3: () => css`
    ${fontService.getFont({ size: 18, lineHeight: 144.44, weight: FontWeight.medium })}
  `,
  /** Название профессии в блоке цели: 22/30. */
  professionTitle: () => css`
    ${fontService.getFont({ size: 22, lineHeight: 136.36, weight: FontWeight.medium })}
  `,
  /** Заголовок группы меню: 13/20. */
  groupTitle: () => css`
    ${fontService.getFont({ size: 13, lineHeight: 153.85, weight: FontWeight.medium })}
  `,
  /** Текст урока: 18 / 17, межстрочный 1.6 (ТЗ 5.3). */
  lesson: () => css`
    ${fontService.getFont({ size: 17, lineHeight: 160 })}

    ${tablet} {
      ${fontService.getFont({ size: 18, lineHeight: 160 })}
    }
  `,
  /** Основной текст раздела. Ниже 16 в карьерном разделе не опускаемся. */
  body: () => css`
    ${fontService.getFont({ size: 16, lineHeight: 150 })}
  `,
  /** Кнопка, активный пункт меню, название дополнительного действия. */
  bodyStrong: () => css`
    ${fontService.getFont({ size: 16, lineHeight: 150, weight: FontWeight.medium })}
  `,
  /**
   * Подпись узла на холсте карты навыков.
   *
   * 16 — нижняя граница основного текста раздела, и здесь она же верхняя:
   * карточек на экране должно помещаться много, а имя технологии всё равно
   * выигрывает у подписи уровней начертанием и кеглем (16 против 14).
   *
   * ⚠️ Прежние 18 объяснялись тем, что «вписать» доводило масштаб до 1.4 — с
   * потолком 1.0 этот довод исчез, а вместе с ним и лишняя ступень шкалы.
   * Худший случай теперь 16 · MIN_ZOOM 0.8 = 12.8 px.
   */
  cardTitle: () => css`
    ${fontService.getFont({ size: 16, lineHeight: 145, weight: FontWeight.medium })}
  `,
  /** Метаданные, подписи, единицы. Нижняя граница ТЗ. */
  meta: () => css`
    ${fontService.getFont({ size: 14, lineHeight: 142.86 })}
  `,
  /** ⚠️ Не для главной /career и меню: там метаданные без жирного (15.09). */
  metaStrong: () => css`
    ${fontService.getFont({ size: 14, lineHeight: 145, weight: FontWeight.medium })}
  `,
};

/** Отступы и радиусы раздела — те же числа, что в ТЗ, в одном месте. */
export const careerLayout = {
  /** ТЗ 0071, п. 2.1: основная ширина содержимого — до 1360. */
  maxWidth: 1360,
  gutterDesktop: 32,
  gutterTablet: 24,
  gutterMobile: 16,
  /** Между основными разделами страницы. */
  sectionGap: 32,
  /** Длинный текст урока не шире этого. */
  readingWidth: 760,
  /** Основная кнопка — от 48, компактная — от 44. */
  primaryButton: 48,
  /** Главная карточка — 12, кнопки и активный пункт меню — 8 (15.09). */
  cardRadius: 12,
  controlRadius: 8,
  /** Оболочка: меню 224 (планшет 200), зазор 40 (24), поля 32 / 24 / 16. */
  shellMenu: 224,
  shellMenuTablet: 200,
  shellGap: 40,
  shellGapTablet: 24,
  /** Колонка главной страницы внутри оболочки. */
  planWidth: 1200,
  cardPadding: 24,
  /** Минимальная зона нажатия. Ниже — нарушение доступности, не вкусовщина. */
  hitTarget: 44,
  /**
   * 0073: плотного класса контролов больше нет. Панель карты и фильтры
   * вакансий — 44 с текстом 14 (ТЗ «финальная доработка», 5.3); при нехватке
   * места ряд переносится, а не ужимается.
   */
  denseGap: 8,
};
